import { FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import { uniqueId } from 'lodash';
import React, { useContext, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import styled from 'styled-components/macro';
import { IfUserHasPermission } from '../../authorisation/IfUserHasPermission';
import { moveElement } from '../../helpers/arrayHelpers';
import { SecondaryButton } from '../../shared/Button';
import { EmptyPlaceholder } from '../../shared/EmptyPlaceholder';
import { FormContext } from '../../shared/form/Form';
import { SearchSelect, SearchSelectOptions } from '../../shared/form/inputs/SearchSelect';
import { Table, TBody, Td, THead, Tr } from '../../shared/Table';
import { colourNeutral07 } from '../../styling/colours';
import { spacing02, spacing06 } from '../../styling/spacing';
import { EditDesignBillOfMaterialsFormModel } from '../designs/edit-bill-of-materials/editDesignBillOfMaterialsFormModel';
import { ViewOrEditQuoteBillOfMaterialsFormModel } from '../quotes/view-or-edit-bill-of-materials/viewOrEditQuoteBillOfMaterialsFormModel';
import { BillOfMaterialsItemField } from './BillOfMaterialsItemField';
import { BillOfMaterialsItemFormModel } from './billOfMaterialsItemFormModel';
import { ActionButtonTh, CurrencyTh, DescriptionTh, DragHandleTh, PartCodeTh } from './BillOfMaterialsItemsTable';
import { BillOfMaterialsPartsById } from './dataForEditBillOfMaterialsForm';
import { QuantityColumnHeader } from './QuantityColumnHeader';

type Props<TFormModel extends EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel> = {
  itemsFieldName: string;
  items: Array<BillOfMaterialsItemFormModel>;
  partsById: BillOfMaterialsPartsById;
  partsOptions: SearchSelectOptions<number | null>;
  setFieldValue: FormikProps<TFormModel>['setFieldValue'];
  setFieldTouched: FormikProps<TFormModel>['setFieldTouched'];
  errors: FormikProps<TFormModel>['errors'];
};

export const BillOfMaterialsItemsField = <
  TFormModel extends EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel
>({
  itemsFieldName,
  items,
  partsById,
  partsOptions,
  setFieldValue,
  setFieldTouched,
  errors,
}: Props<TFormModel>) => {
  const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
  const { isDisabled } = useContext(FormContext);

  const addItem = (fieldArrayRenderProps: FieldArrayRenderProps) => {
    if (selectedPartId == null) {
      return;
    }

    const newItem: BillOfMaterialsItemFormModel = {
      id: uniqueId('bom_item'),
      billOfMaterialsItemId: null,
      partId: selectedPartId,
      quantity: '1',
      costInPounds: null,
      priceInPounds: null,
      isDeleted: false,
      partIsInactiveValidation: null,
    };

    fieldArrayRenderProps.push(newItem);

    setSelectedPartId(null);
  };

  const deleteItem = (
    item: BillOfMaterialsItemFormModel,
    index: number,
    fieldArrayRenderProps: FieldArrayRenderProps,
  ) => {
    if (item.billOfMaterialsItemId != null) {
      setFieldValue(`${itemsFieldName}.${index}.isDeleted`, true);
    } else {
      fieldArrayRenderProps.remove(index);
    }
  };

  const idsOfItemsWhichHaveNotBeenDeleted = items.filter(item => !item.isDeleted).map(item => item.id);

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (destination == null) {
      return;
    }

    const ids = items.map(item => item.id);

    const sourceId = idsOfItemsWhichHaveNotBeenDeleted[source.index];
    const destinationId = idsOfItemsWhichHaveNotBeenDeleted[destination.index];

    const sourceIndex = ids.indexOf(sourceId);
    const destinationIndex = ids.indexOf(destinationId);

    setFieldTouched(`${itemsFieldName}.${sourceIndex}`, false);
    setFieldTouched(`${itemsFieldName}.${destinationIndex}`, false);

    const [newItems] = moveElement(items, items, sourceIndex, destinationIndex);
    setFieldValue(itemsFieldName, newItems);
  };

  return (
    <FieldArray name={itemsFieldName}>
      {fieldArrayRenderProps => (
        <>
          <StyledTable>
            <THead>
              <Tr>
                <DragHandleTh />
                <PartCodeTh>Part Code</PartCodeTh>
                <QuantityColumnHeader fieldArrayName={itemsFieldName} errors={errors} />
                <DescriptionTh>Description</DescriptionTh>
                <CurrencyTh>Cost (£)</CurrencyTh>
                <CurrencyTh>Price (£)</CurrencyTh>
                <ActionButtonTh />
              </Tr>
            </THead>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId={itemsFieldName}>
                {droppableProvided => (
                  <TBody {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                    {items.map((item, index) =>
                      item.isDeleted ? null : (
                        <Draggable
                          draggableId={item.id}
                          key={item.id}
                          index={idsOfItemsWhichHaveNotBeenDeleted.indexOf(item.id)}
                          isDragDisabled={isDisabled}
                        >
                          {(draggableProvided, draggableSnapshot) => (
                            <BillOfMaterialsItemField
                              fieldName={`${itemsFieldName}.${index}`}
                              item={item}
                              partsById={partsById}
                              deleteItem={() => deleteItem(item, index, fieldArrayRenderProps)}
                              errors={errors}
                              draggableProvided={draggableProvided}
                              draggableSnapshot={draggableSnapshot}
                            />
                          )}
                        </Draggable>
                      ),
                    )}
                    {items.filter(item => !item.isDeleted).length === 0 && (
                      <Tr>
                        <Td colSpan={7}>
                          <EmptyPlaceholder message="No items have been added to the Bill of Materials." />
                        </Td>
                      </Tr>
                    )}
                    {droppableProvided.placeholder}
                  </TBody>
                )}
              </Droppable>
            </DragDropContext>
          </StyledTable>
          {!isDisabled && (
            <AddPartContainer>
              <AddPartSearchSelectContainer>
                <SearchSelect<number | null>
                  onChange={setSelectedPartId}
                  value={selectedPartId}
                  options={partsOptions}
                  placeholder="Enter a Part Code"
                  minimumSearchCharacters={1}
                  testId={billOfMaterialsAddPartSearchSelectTestId(itemsFieldName)}
                />
              </AddPartSearchSelectContainer>
              <SecondaryButton
                onClick={() => addItem(fieldArrayRenderProps)}
                disabled={selectedPartId == null}
                data-testid={billOfMaterialsAddPartButtonTestId(itemsFieldName)}
              >
                Add Part
              </SecondaryButton>
            </AddPartContainer>
          )}
        </>
      )}
    </FieldArray>
  );
};

export const billOfMaterialsAddPartSearchSelectTestId = (itemsFieldName: string) =>
  `add-part-search-select-${itemsFieldName}`;

export const billOfMaterialsAddPartButtonTestId = (itemsFieldName: string) => `add-part-button-${itemsFieldName}`;

const StyledTable = styled(Table)`
  table-layout: fixed;
  background-color: ${colourNeutral07};
`;

const AddPartContainer = styled.div`
  margin-top: ${spacing06};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
`;

const AddPartSearchSelectContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 500px;
  margin-right: ${spacing02};
`;
